import T from '@/components/Helpers/Translate'
import { Button } from '@/components/_ui/Button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/_ui/Popover'
import Spinner from '@/components/_ui/Spinner'

import { copyTextToClipboard } from '@/lib/utils/shared/copyTextToClipboard'
import useGlobalStore from '@/store/useGlobalStore'

import Image from 'next/image'

import { useEffect, useState } from 'react'

import { useGetMeReferralCode } from '@/lib/hooks/services/useGetMeReferralCode'
import { cn } from '@/lib/utils'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'

type Props = {
  referralCopyType?: 'link' | 'code'
  hasBottomButton?: boolean
  isAccountMenu?: boolean
  isCheckoutPage?: boolean
  isAccountPopover?: boolean
}

export const ReferralCopy = ({
  referralCopyType = 'link',
  hasBottomButton,
  isAccountMenu,
  isCheckoutPage,
  isAccountPopover = false,
}: Props) => {
  const {
    user: { token },
  } = useGlobalStore()
  const { gtm } = useGTM()

  const { isLoadingMeReferralCode, referralCode } = useGetMeReferralCode()

  const [isReferralLinkCopied, setIsReferralLinkCopied] = useState(false)

  const referralText =
    referralCopyType === 'link'
      ? `${window.location.origin}/c/${referralCode}`
      : referralCode

  const analyticsEventLocation = () => {
    if (isAccountPopover) {
      return 'profile_menu'
    }
    if (isAccountMenu) {
      return 'account_menu'
    }
    if (isCheckoutPage) {
      return 'thank_you_page'
    }
    if (hasBottomButton) {
      return 'account_referral_link'
    }

    return 'profile_menu'
  }

  const handleCopy = () => {
    if (!referralCode || !referralText) {
      return
    }

    copyTextToClipboard({ text: referralText })
    if (referralCopyType === 'code') {
      gtm.userReferralCodeCopied()

      mixpanelService
        .trackEvent(MixpanelEvent.USER_REFERRAL_CODE_COPIED, {
          event_location: analyticsEventLocation(),
        })
        .catch(() => {})
    } else {
      gtm.userReferralLinkCopied(analyticsEventLocation())

      mixpanelService
        .trackEvent(MixpanelEvent.USER_REFERRAL_LINK_COPIED, {
          event_location: analyticsEventLocation(),
        })
        .catch(() => {})
    }

    setIsReferralLinkCopied(true)
  }

  useEffect(() => {
    if (!isReferralLinkCopied) return

    const timer = setTimeout(() => {
      setIsReferralLinkCopied(false)
    }, 3000)

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer)
  }, [isReferralLinkCopied])

  if (!token) return null

  const renderReferralCopy = () => (
    <>
      <div
        className={cn(
          'flex w-full rounded-lg bg-white p-2',
          isAccountMenu ? '' : 'border border-primary',
        )}
      >
        {isLoadingMeReferralCode ? (
          <div className="m-2">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="flex w-full items-center justify-between overflow-auto">
              <p className="max-w-[250px] overflow-hidden text-xs text-ellipsis">
                {referralText}
              </p>
            </div>

            <Popover open={isReferralLinkCopied}>
              <PopoverTrigger>
                <Button
                  onClick={handleCopy}
                  size="icon"
                  variant="icon"
                  data-test="referral-copy-button"
                >
                  {isReferralLinkCopied ? (
                    <Image
                      alt="copied"
                      height={20}
                      src="/icons/check.svg"
                      width={20}
                    />
                  ) : (
                    <Image
                      alt="copy"
                      height={20}
                      src="/icons/copy.svg"
                      width={20}
                    />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" className="flex w-full justify-center">
                <p className="text-xs">
                  <T>copied_to_clipboard</T>
                </p>
              </PopoverContent>
            </Popover>
          </>
        )}
      </div>

      {hasBottomButton && (
        <Button disabled={isReferralLinkCopied} size="lg" onClick={handleCopy}>
          <T>copy_link</T>
        </Button>
      )}
    </>
  )

  if (isCheckoutPage) {
    return (
      <div className="mb-3 flex flex-col rounded-lg bg-white px-4 py-10 text-primary md:px-8">
        <h3 className="mb-5 text-xl font-bold">
          <span>
            <T>refer_friend</T>
          </span>
        </h3>

        <span className="mb-2.5 text-xl">
          <T>your_referral_link</T>
        </span>
        {renderReferralCopy()}
      </div>
    )
  }

  if (!isAccountMenu) {
    return renderReferralCopy()
  }

  return (
    <div className="mb-3 flex max-w-full flex-col gap-y-3 rounded-lg bg-sky-100 p-3 text-primary">
      <h3 className="text-xl font-bold">
        <span>
          <T>your_referral_link</T>
        </span>
      </h3>

      {renderReferralCopy()}

      <span className="text-xs">
        <T>refer_friend</T>
      </span>
    </div>
  )
}
